import { reportAsyncError } from "@istari-frontend/shared";
import type { V3 } from "@istari/istari-client";
import { useQueries } from "@tanstack/react-query";
import type { RefObject } from "react";
import { useEffect, useRef } from "react";

import { systemQueryOptions } from "~/systems/data-source/system-query-options";
import { userQueryOptions } from "~/user/data-source/user-query-options";
import { workflowLogQueryOptions } from "~/workflow-log/data-source/api/workflow-log-query-options";

import { toNotificationActorName } from "./resolve-notification-actor-name";

export type WorkflowLogDetail = { title: string | null; createdById: string | null };

export type WorkflowLogEnrichment = {
  workflowLogDetails: WorkflowLogDetail[];
  workflowLogCreatorNames: (string | null)[];
  workflowLogSystemNames: (string | null)[];
};

type Failure = { key: string; error: unknown; context: Record<string, unknown> };

function reportNewFailures(failures: Failure[], reportedKeys: RefObject<Set<string>>, label: string) {
  failures.forEach((failure) => {
    if (reportedKeys.current.has(failure.key)) return;
    reportedKeys.current.add(failure.key);
    reportAsyncError(label, failure.error, failure.context);
  });
}

export function useWorkflowLogEnrichment(
  events: V3.UserWorkflowLogCompletedNotificationEvent[],
): WorkflowLogEnrichment {
  const reportedWorkflowLogFailures = useRef<Set<string>>(new Set());
  const reportedCreatorFailures = useRef<Set<string>>(new Set());

  // combine applies replaceEqualDeep to the result, keeping stable references when data is
  // unchanged, so callers can feed both arrays straight into a useMemo dep list. It must stay a
  // pure mapping — no side effects here, since combine re-runs on every render these queries
  // participate in, not once per failure.
  const { details: workflowLogDetails, failures: workflowLogFailures } = useQueries({
    queries: events.map((event) => workflowLogQueryOptions.detail(event.systemId, event.workflowLogEntryId)),
    combine: (results) => ({
      details: results.map((result) =>
        result.status === "error"
          ? { title: null, createdById: null }
          : { title: result.data?.title ?? null, createdById: result.data?.createdById ?? null },
      ),
      failures: results.flatMap((result, index): Failure[] => {
        if (result.status !== "error") return [];
        const event = events[index];
        return [
          {
            key: `${event.systemId}:${event.workflowLogEntryId}`,
            error: result.error,
            context: { systemId: event.systemId, workflowLogEntryId: event.workflowLogEntryId },
          },
        ];
      }),
    }),
  });

  useEffect(() => {
    reportNewFailures(workflowLogFailures, reportedWorkflowLogFailures, "notifications-workflow-log-lookup-failed");
  }, [workflowLogFailures]);

  const { names: workflowLogCreatorNames, failures: creatorFailures } = useQueries({
    queries: workflowLogDetails.map((detail) => ({
      ...userQueryOptions.byId(detail.createdById ?? ""),
      enabled: !!detail.createdById,
    })),
    combine: (results) => ({
      names: results.map((result) => (result.status === "error" ? null : toNotificationActorName(result.data))),
      failures: results.flatMap((result, index): Failure[] => {
        if (result.status !== "error") return [];
        const userId = workflowLogDetails[index].createdById;
        if (!userId) return [];
        return [{ key: userId, error: result.error, context: { userId } }];
      }),
    }),
  });

  useEffect(() => {
    reportNewFailures(creatorFailures, reportedCreatorFailures, "notifications-workflow-log-creator-lookup-failed");
  }, [creatorFailures]);

  const reportedSystemFailures = useRef<Set<string>>(new Set());

  const { names: workflowLogSystemNames, failures: systemFailures } = useQueries({
    queries: events.map((event) => systemQueryOptions(event.systemId)),
    combine: (results) => ({
      names: results.map((result) => (result.status === "error" ? null : (result.data?.name ?? null))),
      failures: results.flatMap((result, index): Failure[] => {
        if (result.status !== "error") return [];
        const event = events[index];
        return [{ key: event.systemId, error: result.error, context: { systemId: event.systemId } }];
      }),
    }),
  });

  useEffect(() => {
    reportNewFailures(systemFailures, reportedSystemFailures, "notifications-workflow-log-system-lookup-failed");
  }, [systemFailures]);

  return { workflowLogDetails, workflowLogCreatorNames, workflowLogSystemNames };
}
